import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Form, InputGroup, Alert } from 'react-bootstrap';
import { FaEye, FaEyeSlash, FaFingerprint, FaUser, FaCloudDownloadAlt, FaDownload, FaSyncAlt, FaArrowRight } from 'react-icons/fa';
import { NativeBiometric } from 'capacitor-native-biometric';
import { Capacitor } from '@capacitor/core';
import logoAnimacion from '../assets/logo-animacion.mp4';
import logoAnimacionOscuro from '../assets/logo-animacion-oscuro.mp4';
import logoIso from '../assets/sisnetelp.png';
import { useTemaLogin, LEMA_POR_DEFECTO } from '../hooks/useTemaLogin';
import useTema from '../hooks/useTema';
import { MOTIVO_INACTIVIDAD } from '../components/CierreInactividad';
import './Login.css';

const BIOMETRIC_SERVER = 'com.sisnetel.app';

// Se lee de package.json (igual que el Menú) para que no se quede desactualizada
const APP_VERSION: string = (require('../../package.json') as { version: string }).version;

// 'android' en el APK, 'web' en el navegador. n8n solo debe exigir versión
// al APK: la web la publica EasyPanel y siempre está en la última.
const PLATAFORMA: 'android' | 'web' = Capacitor.isNativePlatform() ? 'android' : 'web';

/**
 * Respuesta del login cuando la versión de la app ya no se acepta.
 * n8n la devuelve con `estado: 'actualizar'` (o con código HTTP 426).
 */
interface Actualizacion {
  minima?: string;
  apk?: string;      // enlace de descarga del APK (Drive)
  mensaje?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // CierreInactividad manda aquí con este motivo tras 5 minutos sin actividad
  const cerradaPorInactividad = (location.state as { motivo?: string } | null)?.motivo === MOTIVO_INACTIVIDAD;

  const [cedula, setCedula]             = useState<string>('');
  const [contrasena, setContrasena]     = useState<string>('');
  const [error, setError]               = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [hasCredentials, setHasCredentials] = useState<boolean>(false);
  const [showForm, setShowForm]         = useState<boolean>(false);
  const [loading, setLoading]           = useState<boolean>(false);
  const [splashDone, setSplashDone]     = useState<boolean>(false);

  // Cuando n8n rechaza la versión, se tapa el login con la pantalla de
  // actualizar. Solo se quita con "Ya la instalé" (vuelve a intentar).
  const [actualizacion, setActualizacion] = useState<Actualizacion | null>(null);

  // Tema remoto (imagen, lema, color y aviso). Si no hay backend o falla,
  // devuelve vacío y se usa el diseño incluido en el APK.
  const tema = useTemaLogin();

  // El video del logo trae el fondo incrustado (un MP4 no admite transparencia),
  // así que hay una versión por tema y se elige según el que esté pintado.
  const { temaAplicado } = useTema();
  const videoLogo = temaAplicado === 'oscuro' ? logoAnimacionOscuro : logoAnimacion;
  const [imagenFallo, setImagenFallo]   = useState<boolean>(false);
  const usarImagenRemota = Boolean(tema.imagen) && !imagenFallo;

  const EyeIcon         = FaEye         as unknown as React.FC;
  const EyeSlashIcon    = FaEyeSlash    as unknown as React.FC;
  const FingerprintIcon = FaFingerprint as unknown as React.FC;
  const UserIcon        = FaUser        as unknown as React.FC;
  const CloudDownIcon   = FaCloudDownloadAlt as unknown as React.FC;
  const DownloadIcon    = FaDownload    as unknown as React.FC;
  const SyncIcon        = FaSyncAlt     as unknown as React.FC;
  const ArrowRightIcon  = FaArrowRight  as unknown as React.FC;

  useEffect(() => {
    checkBiometric();
    // Splash: el logo pulsa ~2.2s y luego entra el login escalonado
    const t = setTimeout(() => setSplashDone(true), 2200);
    return () => clearTimeout(t);
  }, []);

  const checkBiometric = async () => {
    try {
      const result  = await NativeBiometric.isAvailable();
      const hasSaved = localStorage.getItem('biometric_enabled') === 'true';
      setHasCredentials(result.isAvailable && hasSaved);
    } catch {
      setHasCredentials(false);
    }
  };

  const doLogin = async (ced: string, pass: string) => {
    const url = process.env.REACT_APP_N8N_URL_LOGIN;
    if (!url) { setError('Error de configuración: Falta la URL de login.'); return; }

    // Tres requisitos para entrar: cédula, contraseña y versión de la app.
    // n8n compara `version` con la mínima permitida y, si no alcanza,
    // responde `estado: 'actualizar'` (o HTTP 426) con el enlace del APK.
    let data: { estado?: string; datos?: unknown[] } & Actualizacion;
    try {
      const response = await axios.post(url, {
        cedula: ced,
        codigo: pass,
        version: APP_VERSION,
        plataforma: PLATAFORMA,
      });
      data = response.data ?? {};
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 426) {
        data = { estado: 'actualizar', ...(err.response.data ?? {}) };
      } else {
        throw err;
      }
    }

    if (data.estado === 'actualizar') {
      setActualizacion({ minima: data.minima, apk: data.apk, mensaje: data.mensaje });
      return;
    }
    if (data.estado === 'exito' && Array.isArray(data.datos) && data.datos[0]) {
      localStorage.setItem('clientData', JSON.stringify(data.datos[0]));
      navigate('/menu'); // la transición del logo la maneja PageTransition
    } else {
      setError('Credenciales incorrectas o usuario no encontrado.');
    }
  };

  /** Abre el enlace de descarga fuera de la app (Drive), o recarga la web. */
  const descargarActualizacion = () => {
    if (PLATAFORMA === 'web') { window.location.reload(); return; }
    if (actualizacion?.apk) window.open(actualizacion.apk, '_system');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await doLogin(cedula, contrasena);
      try {
        const available = await NativeBiometric.isAvailable();
        if (available.isAvailable) {
          await NativeBiometric.setCredentials({ username: cedula, password: contrasena, server: BIOMETRIC_SERVER });
          localStorage.setItem('biometric_enabled', 'true');
          setHasCredentials(true);
        }
      } catch { /* biometría no disponible */ }
    } catch {
      setError('Error al comunicarse con la API.');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometric = async () => {
    setError('');
    setLoading(true);
    try {
      await NativeBiometric.verifyIdentity({
        reason: 'Confirma tu identidad para ingresar',
        title: 'SisNetel — Acceso biométrico',
        subtitle: 'Usa tu huella registrada',
        description: 'Toca el sensor de huella',
      });
      const credentials = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
      await doLogin(credentials.username, credentials.password);
    } catch {
      setError('Autenticación biométrica fallida. Usa usuario y contraseña.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`ln-wrapper${splashDone ? ' ln-ready' : ''}`}>
      {/* ── Splash: logo pulsante ── */}
      <div className={`ln-splash${splashDone ? ' ln-splash--done' : ''}`}>
        <div className="ln-splash-logo">
          <img src={logoIso} alt="SisNetel" />
        </div>
      </div>

      {/* ── Pantalla principal ── */}
      <div className="ln-logo-row ln-st ln-st-1">
        <img src={logoIso} alt="SisNetel" className="ln-logo-img" />
        <div>
          <div className="ln-brand">SISNETEL</div>
          <div className="ln-subbrand">FIBER EC</div>
        </div>
      </div>

      {/* ── Ilustración: imagen del tema remoto, o la animación del APK ── */}
      <div className="ln-illus-wrap ln-st ln-st-2">
        <div className={`ln-stage${usarImagenRemota ? '' : ' ln-stage--video'}`}>
          {usarImagenRemota ? (
            <img
              src={tema.imagen}
              alt=""
              className="ln-tema-img"
              aria-hidden="true"
              onError={() => setImagenFallo(true)}
            />
          ) : (
            <video
              key={temaAplicado}
              src={videoLogo}
              className="ln-logo-video"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              aria-label="SisNetel Fiber — Servicios de Internet"
            />
          )}
        </div>
        <p className="ln-tagline">{tema.lema || LEMA_POR_DEFECTO}</p>
        {tema.aviso && (
          <p className="ln-aviso" role="status">{tema.aviso}</p>
        )}
      </div>

      {/* ── Contenido ── */}
      <div className="ln-card ln-st ln-st-3">
        {cerradaPorInactividad && !error && (
          <div className="ln-alert ln-alert--info" role="status">
            <span className="ln-alert-icon">⏱️</span> Tu sesión se cerró tras 5 minutos sin actividad. Vuelve a ingresar.
          </div>
        )}
        {error && !showForm && (
          <div className="ln-alert" role="alert">
            <span className="ln-alert-icon">⚠</span> {error}
          </div>
        )}

        <h2 className="ln-heading">Bienvenido</h2>
        <p className="ln-subheading">Elige cómo deseas ingresar</p>
        <div className="ln-btn-grid">
          <button
            className="ln-access-btn"
            onClick={() => { setShowForm(true); setError(''); }}
            disabled={loading}
          >
            <div className="ln-access-icon">
              <UserIcon />
            </div>
            <span>Usuario y<br/>contraseña</span>
          </button>
          <button
            className="ln-access-btn"
            onClick={handleBiometric}
            disabled={loading || !hasCredentials}
          >
            <div className="ln-access-icon">
              <FingerprintIcon />
            </div>
            <span>Huella /<br/>Face ID</span>
          </button>
        </div>
        <button className="ln-link">
          ¿Olvidaste tu contraseña?
        </button>
      </div>

      <p className="ln-version ln-st ln-st-4">Versión {APP_VERSION}</p>

      {/* ── Pantalla de actualización obligatoria ──
          Tapa el login y el formulario (z-index por encima de ambos). No se
          puede cerrar: solo "Ya la instalé", que vuelve a intentar el login. */}
      {actualizacion && (
        <div className="ln-update" role="alertdialog" aria-modal="true" aria-labelledby="ln-update-titulo">
          <div className="ln-update-card">
            <div className="ln-update-brand">
              <img src={logoIso} alt="SisNetel" className="ln-logo-img ln-logo-img--sm" />
              <div>
                <div className="ln-brand">SISNETEL</div>
                <div className="ln-subbrand">FIBER EC</div>
              </div>
            </div>

            {/* Insignia con anillos que se expanden: llama la atención sin gritar */}
            <div className="ln-update-badge" aria-hidden="true">
              <span className="ln-update-ring" />
              <span className="ln-update-ring ln-update-ring--2" />
              <div className="ln-update-badge-core"><CloudDownIcon /></div>
            </div>

            <h2 id="ln-update-titulo" className="ln-update-title">Nueva versión disponible</h2>
            <p className="ln-update-text">
              {actualizacion.mensaje || 'Esta versión ya no se acepta. Instala la versión nueva para seguir usando la app.'}
            </p>

            <div className="ln-update-versions">
              <div className="ln-update-chip">
                <small>Instalada</small>
                <strong>{APP_VERSION}</strong>
              </div>
              <span className="ln-update-arrow"><ArrowRightIcon /></span>
              <div className="ln-update-chip ln-update-chip--new">
                <small>Requerida</small>
                <strong>{actualizacion.minima || '—'}</strong>
              </div>
            </div>

            {PLATAFORMA === 'web' ? (
              <button type="button" className="ln-submit ln-update-btn" onClick={descargarActualizacion}>
                <SyncIcon /> Recargar la página
              </button>
            ) : actualizacion.apk ? (
              <button type="button" className="ln-submit ln-update-btn" onClick={descargarActualizacion}>
                <DownloadIcon /> Descargar actualización
              </button>
            ) : (
              <p className="ln-update-sin-enlace">Pide el instalador nuevo al administrador.</p>
            )}

            <button type="button" className="ln-link" onClick={() => setActualizacion(null)}>
              Ya la instalé, volver a intentar
            </button>

            {PLATAFORMA === 'android' && (
              <p className="ln-update-foot">Tu sesión y tus datos se conservan al actualizar.</p>
            )}
          </div>
        </div>
      )}

      {/* ── Pantalla de formulario (se desliza desde la derecha) ── */}
      <div className={`ln-form-screen${showForm ? ' ln-form-screen--open' : ''}`}>
        <div className="ln-form-header">
          <button
            className="ln-back-arrow"
            onClick={() => { setShowForm(false); setError(''); }}
            aria-label="Volver"
          >
            ←
          </button>
          <div className="ln-form-header-brand">
            <img src={logoIso} alt="SisNetel" className="ln-logo-img ln-logo-img--sm" />
            <span className="ln-form-header-title">Iniciar sesión</span>
          </div>
        </div>

        <div className="ln-form-body">
          {error && showForm && (
            <div className="ln-alert" role="alert">
              <span className="ln-alert-icon">⚠</span> {error}
            </div>
          )}

          <h2 className="ln-heading">Bienvenido</h2>
          <p className="ln-subheading">Ingresa con tu cuenta</p>

          <Form onSubmit={handleSubmit} noValidate>
              <Form.Group className="ln-field" controlId="formCedula">
                <Form.Label className="ln-label">Cédula</Form.Label>
                <Form.Control
                  className="ln-input"
                  type="text"
                  placeholder="Ingrese su cédula"
                  value={cedula}
                  onChange={e => setCedula(e.target.value)}
                  required
                  autoComplete="username"
                />
              </Form.Group>

              <Form.Group className="ln-field" controlId="formContrasena">
                <Form.Label className="ln-label">Contraseña</Form.Label>
                <InputGroup>
                  <Form.Control
                    className="ln-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Ingrese su contraseña"
                    value={contrasena}
                    onChange={e => setContrasena(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <InputGroup.Text
                    className="ln-eye"
                    onClick={() => setShowPassword(p => !p)}
                    role="button"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <button
                type="submit"
                className={`ln-submit${loading ? ' ln-submit--loading' : ''}`}
                disabled={loading}
              >
                {loading ? <span className="ln-spinner" /> : 'Ingresar'}
              </button>
          </Form>

          <button className="ln-link" style={{ marginTop: '0.5rem' }}>
            ¿Olvidaste tu contraseña?
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
